import { ResponseError } from '@evimo/client/runtime';
import {
  isAuthorizationErrorReason,
  type AuthorizationErrorReason,
} from '@evimo/types/enums/authorization-error-reason';

export class AuthorizationApiError extends Error {
  public readonly reason?: AuthorizationErrorReason;

  public readonly statusCode?: number;

  constructor(
    message: string,
    options?: {
      reason?: AuthorizationErrorReason;
      statusCode?: number;
    },
  ) {
    super(message);
    this.name = 'AuthorizationApiError';
    this.reason = options?.reason;
    this.statusCode = options?.statusCode;
  }
}

type ApiErrorPayload = {
  message?: string;
  reason?: unknown;
};

export function parseAuthorizationApiError(
  response: Response,
  payload: ApiErrorPayload,
  fallback: string,
): AuthorizationApiError {
  const reason = isAuthorizationErrorReason(payload.reason)
    ? payload.reason
    : undefined;

  return new AuthorizationApiError(payload.message?.trim() || fallback, {
    reason,
    statusCode: response.status,
  });
}

export async function invokeAuthorizationClient<T>(
  fallbackMessage: string,
  action: () => Promise<T>,
): Promise<T> {
  try {
    return await action();
  } catch (error) {
    if (error instanceof ResponseError) {
      const payload = await error.response
        .json()
        .catch(() => ({ message: error.response.statusText }));

      throw parseAuthorizationApiError(
        error.response,
        payload,
        fallbackMessage,
      );
    }

    throw error;
  }
}
